/**
 * LeadSourcesAdmin (LEAD-SOURCE-02, Seth 2026-09-01: "where is the list in
 * settings to add, archive, etc") — the managed lead-source picklist. Every
 * option the 360 card and reports use lives here: add, rename the label,
 * archive / bring back. Codes are permanent once used (they're stamped on
 * history), so archive hides an option from pickers without rewriting the
 * past. Usage counts ride in from the same RPC the report uses.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Megaphone, Plus, Check, Pencil, Archive, RotateCcw, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { onErrToast } from "@/components/ui/mutationToast";
import { useLeadSourceOptions, useAddLeadSourceOption } from "@/features/customer-360/leadSource";

interface UsageRow { code: string; customers: number; new_this_year: number; overrides: number }

function useLeadSourceUsage() {
  return useQuery({
    queryKey: ["lead_source_usage"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Map<string, UsageRow>> => {
      const { data, error } = await supabase.rpc("lead_source_report");
      if (error) throw error;
      const m = new Map<string, UsageRow>();
      for (const r of (data ?? []) as (UsageRow & { label: string })[]) m.set(r.code, r);
      return m;
    },
  });
}

function usePatchLeadSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { code: string; patch: { label?: string; active?: boolean } }) => {
      const { error } = await supabase.from("lead_source").update(v.patch).eq("code", v.code);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["lead_source_options"] });
      void qc.invalidateQueries({ queryKey: ["lead_source_usage"] });
    },
  });
}

export function LeadSourcesAdmin() {
  const { data: opts } = useLeadSourceOptions();
  const usage = useLeadSourceUsage().data;
  const patch = usePatchLeadSource();
  const add = useAddLeadSourceOption();
  const [showArchived, setShowArchived] = useState(false);
  const [editCode, setEditCode] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [adding, setAdding] = useState(false);
  const [nc, setNc] = useState("");
  const [nl, setNl] = useState("");

  const rows = useMemo(() => {
    const list = (opts ?? []).filter((o) => (showArchived ? true : o.active));
    return [...list].sort((a, b) => (usage?.get(b.code)?.customers ?? 0) - (usage?.get(a.code)?.customers ?? 0) || a.label.localeCompare(b.label));
  }, [opts, usage, showArchived]);
  const archivedCount = (opts ?? []).filter((o) => !o.active).length;

  return (
    <div className="max-w-3xl space-y-4">
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 bg-gradient-to-r from-amber-50/70 via-transparent to-transparent px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
              <Megaphone className="h-[18px] w-[18px]" />
            </span>
            <div className="flex items-center gap-2 text-[14.5px] font-bold tracking-tight text-slate-900">
              Lead Sources
              <InfoTip label="About lead sources">
                The picklist behind the 360&apos;s Lead Source card and the Lead Sources report. Archiving hides an option from
                pickers; history keeps its code.
              </InfoTip>
            </div>
          </div>
          <button type="button" onClick={() => setAdding((v) => !v)}
            className={cn("inline-flex shrink-0 items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors",
              adding ? "bg-brand-700 text-white" : "border border-brand-200 bg-white text-brand-700 hover:bg-brand-50")}>
            <Plus className="h-3.5 w-3.5" /> Add Source
          </button>
        </div>

        {adding && (
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-brand-50/40 px-4 py-3">
            <input value={nc} onChange={(e) => setNc(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12))}
              placeholder="CODE (e.g. NEXTDR)"
              className="w-40 rounded-md border border-hairline bg-white px-2.5 py-1.5 font-mono text-sm uppercase focus:border-brand-500 focus:outline-none" />
            <input value={nl} onChange={(e) => setNl(e.target.value)} placeholder="Label people see (e.g. Nextdoor)"
              className="min-w-[220px] flex-1 rounded-md border border-hairline bg-white px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none" />
            <button type="button" disabled={!nc.trim() || !nl.trim() || add.isPending}
              onClick={() => add.mutate({ code: nc, label: nl }, {
                onSuccess: () => { setAdding(false); setNc(""); setNl(""); },
                onError: onErrToast("Couldn't add it — is that code already taken?"),
              })}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-brand-800 disabled:opacity-50">
              {add.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Add
            </button>
          </div>
        )}

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-hairline bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              <th className="px-4 py-2">Source</th>
              <th className="px-3 py-2">Code</th>
              <th className="px-3 py-2 text-right">
                <span className="inline-flex items-center gap-1.5">
                  Customers
                  <InfoTip label="How customers are counted">
                    A customer&apos;s source is their Pegasus correction if there is one, otherwise their first invoice&apos;s source
                    code, counted only when it&apos;s a real source (not &ldquo;existing customer&rdquo;). Reports → Lead Sources
                    shows the same numbers.
                  </InfoTip>
                </span>
              </th>
              <th className="px-3 py-2 text-right">New This Yr</th>
              <th className="w-24 px-3 py-2 text-right" />
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => {
              const u = usage?.get(o.code);
              const editing = editCode === o.code;
              return (
                <tr key={o.code} className={cn("border-b border-slate-50 last:border-0", !o.active && "opacity-50")}>
                  <td className="px-4 py-2">
                    {editing ? (
                      <form className="flex items-center gap-1.5" onSubmit={(e) => {
                        e.preventDefault();
                        patch.mutate({ code: o.code, patch: { label: editLabel.trim() || o.label } }, { onSuccess: () => setEditCode(null), onError: onErrToast("Couldn't rename it") });
                      }}>
                        <input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} autoFocus
                          className="w-48 rounded-md border border-hairline px-2 py-1 text-sm focus:border-brand-500 focus:outline-none" />
                        <button type="submit" className="rounded-md bg-brand-700 px-2 py-1 text-[11px] font-semibold text-white">Save</button>
                        <button type="button" onClick={() => setEditCode(null)} className="text-[11px] text-slate-400 hover:text-slate-600">Cancel</button>
                      </form>
                    ) : (
                      <span className="font-medium text-slate-800">
                        {o.label}
                        {!o.active && <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">archived</span>}
                        {o.category === "custom" && <span className="ml-2 rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold text-violet-600">added in Pegasus</span>}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-400">{o.code}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">{u?.customers?.toLocaleString() ?? "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{u?.new_this_year?.toLocaleString() ?? "—"}</td>
                  <td className="px-3 py-2">
                    <span className="flex items-center justify-end gap-1">
                      <button type="button" onClick={() => { setEditCode(o.code); setEditLabel(o.label); }}
                        title="Rename the label (the code never changes)"
                        className="rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-600">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      {o.active ? (
                        <button type="button" onClick={() => patch.mutate({ code: o.code, patch: { active: false } }, { onError: onErrToast("Couldn't archive it") })}
                          title="Archive — hides it from pickers; existing customers keep it"
                          className="rounded p-1 text-slate-300 hover:bg-amber-50 hover:text-amber-600">
                          <Archive className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <button type="button" onClick={() => patch.mutate({ code: o.code, patch: { active: true } }, { onError: onErrToast("Couldn't restore it") })}
                          title="Bring it back into the pickers"
                          className="rounded p-1 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600">
                          <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {archivedCount > 0 && (
          <div className="border-t border-slate-100 px-4 py-2">
            <button type="button" onClick={() => setShowArchived((v) => !v)}
              className="text-xs font-medium text-slate-400 hover:text-slate-600">
              {showArchived ? "Hide" : "Show"} {archivedCount} archived
            </button>
          </div>
        )}
      </Card>
    </div>
  );
}
